import React from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBInput,
} from "mdb-react-ui-kit";
import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";

const PERIODICIDADES = [
  { value: "MENSUAL",    label: "Mensual" },
  { value: "BIMESTRAL",  label: "Bimestral (cada 2 meses)" },
  { value: "TRIMESTRAL", label: "Trimestral (cada 3 meses)" },
  { value: "ANUAL",      label: "Anual" },
];

/**
 * Modal para crear o editar un Gasto Periódico.
 *
 * Props:
 *  - show:            boolean
 *  - gastoToEdit:     objeto (si es edición) | null (si es creación)
 *  - cuentas:         array de cuentas del usuario
 *  - categorias:      array de categorías del usuario
 *  - onClose():       callback al cerrar sin guardar
 *  - onSaved():       callback tras guardar con éxito
 *  - onLoading(bool): callback para spinner externo
 */
export class GastoPeriodicoModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.buildInitialState(props.gastoToEdit);
  }

  buildInitialState(gasto) {
    if (gasto) {
      return {
        concepto:         gasto.concepto || "",
        importeEstimado:  gasto.importeEstimado != null ? String(gasto.importeEstimado) : "",
        idCategoria:      gasto.iDCategoriaFK != null ? String(gasto.iDCategoriaFK) : "",
        idCuenta:         gasto.iDCuentaFK != null ? String(gasto.iDCuentaFK) : "",
        periodicidad:     gasto.periodicidad || "MENSUAL",
        fechaInicio:      gasto.fechaInicio ? this.toInputDate(gasto.fechaInicio) : "",
        fechaFin:         gasto.fechaFin    ? this.toInputDate(gasto.fechaFin)    : "",
        errorMsg:         "",
      };
    }
    const today = new Date().toISOString().split("T")[0];
    return {
      concepto: "", importeEstimado: "", idCategoria: "", idCuenta: "",
      periodicidad: "MENSUAL", fechaInicio: today, fechaFin: "", errorMsg: "",
    };
  }

  toInputDate(dateVal) {
    if (!dateVal) return "";
    try {
      return new Date(dateVal).toISOString().split("T")[0];
    } catch {
      return "";
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.gastoToEdit !== this.props.gastoToEdit ||
        prevProps.show !== this.props.show) {
      this.setState(this.buildInitialState(this.props.gastoToEdit));
    }
  }

  validate() {
    const { concepto, importeEstimado, idCuenta, periodicidad, fechaInicio } = this.state;
    if (!concepto.trim()) return "El concepto es obligatorio.";
    if (!importeEstimado || isNaN(parseFloat(importeEstimado)) || parseFloat(importeEstimado) <= 0)
      return "El importe estimado debe ser un número positivo.";
    if (!idCuenta) return "Debes seleccionar una cuenta.";
    if (!periodicidad) return "Debes seleccionar la periodicidad.";
    if (!fechaInicio) return "La fecha de inicio es obligatoria.";
    return null;
  }

  async doSave() {
    const error = this.validate();
    if (error) { this.setState({ errorMsg: error }); return; }

    this.setState({ errorMsg: "" });
    this.props.onLoading(true);

    const { gastoToEdit } = this.props;
    const { concepto, importeEstimado, idCategoria, idCuenta, periodicidad, fechaInicio, fechaFin } = this.state;

    const body = {
      concepto: concepto.trim(),
      importeEstimado: parseFloat(importeEstimado),
      idCategoria: idCategoria ? parseInt(idCategoria, 10) : null,
      idCuenta: parseInt(idCuenta, 10),
      periodicidad,
      fechaInicio,
      fechaFin: fechaFin || null,
    };

    const isEdit = !!gastoToEdit;
    if (isEdit) body.idGastoPeriodico = gastoToEdit.iDGastoPeriodico;

    const requestOptions = {
      method: isEdit ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    };

    const endpoint = isEdit ? "updateGastoPeriodico" : "insertGastoPeriodico";

    await doFetch("CCON", endpoint, requestOptions)
      .then((r) => r.json())
      .then((data) => {
        if (data.STATE === "SUCCESS") {
          this.props.onSaved();
        } else {
          this.setState({ errorMsg: data.MESSAGE || "Error al guardar el gasto periódico." });
        }
      })
      .catch(() => this.setState({ errorMsg: "Error de conexión." }))
      .finally(() => this.props.onLoading(false));
  }

  render() {
    const { show, gastoToEdit, cuentas, categorias } = this.props;
    const { concepto, importeEstimado, idCategoria, idCuenta, periodicidad,
            fechaInicio, fechaFin, errorMsg } = this.state;
    const isEdit = !!gastoToEdit;

    const getCuentaId = (c) => c?.IDCuenta || c?.iDCuenta || c?.id || c?.value;
    const getCuentaNombre = (c) => c?.nombre || c?.Nombre || c?.label || `Cuenta ${getCuentaId(c)}`;
    const cuentasOperativas = (cuentas || []).filter((c) => {
      const tipo = String(c?.tipo || c?.Tipo || "").toUpperCase();
      return tipo !== "BUDGET";
    });

    return (
      <MDBModal show={show} onHide={() => this.props.onClose()} tabIndex="-1">
        <MDBModalDialog size="md">
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                {isEdit ? "Editar gasto periódico" : "Nuevo gasto periódico"}
              </MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={() => this.props.onClose()} />
            </MDBModalHeader>

            <MDBModalBody>
              <div className="mb-3">
                <MDBInput
                  label="Concepto *"
                  value={concepto}
                  onChange={(e) => this.setState({ concepto: e.target.value })}
                />
              </div>

              <div className="mb-3">
                <MDBInput
                  label="Importe estimado (€) *"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={importeEstimado}
                  onChange={(e) => this.setState({ importeEstimado: e.target.value })}
                />
              </div>

              <div className="mb-3">
                <label className="form-label text-muted small mb-1">Periodicidad *</label>
                <select
                  className="form-select"
                  value={periodicidad}
                  onChange={(e) => this.setState({ periodicidad: e.target.value })}
                >
                  {PERIODICIDADES.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label text-muted small mb-1">Cuenta *</label>
                <select
                  className="form-select"
                  value={idCuenta}
                  onChange={(e) => this.setState({ idCuenta: e.target.value })}
                >
                  <option value="">— Selecciona cuenta —</option>
                  {cuentasOperativas.map((c) => (
                    <option key={String(getCuentaId(c))} value={String(getCuentaId(c))}>
                      {getCuentaNombre(c)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label text-muted small mb-1">Categoría</label>
                <select
                  className="form-select"
                  value={idCategoria}
                  onChange={(e) => this.setState({ idCategoria: e.target.value })}
                >
                  <option value="">— Sin categoría —</option>
                  {(categorias || []).map((c) => (
                    <option key={c.IDCategoria || c.iDCategoria} value={String(c.IDCategoria || c.iDCategoria)}>
                      {c.nombre || c.Nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <MDBInput
                  label="Fecha de inicio *"
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => this.setState({ fechaInicio: e.target.value })}
                />
              </div>

              <div className="mb-3">
                <MDBInput
                  label="Fecha de fin (opcional)"
                  type="date"
                  value={fechaFin}
                  onChange={(e) => this.setState({ fechaFin: e.target.value })}
                />
                <div className="form-text text-muted">Deja vacío si el gasto no tiene fecha de finalización.</div>
              </div>

              {errorMsg && (
                <div className="alert alert-danger py-2">{errorMsg}</div>
              )}
            </MDBModalBody>

            <MDBModalFooter>
              <MDBBtn color="secondary" outline onClick={() => this.props.onClose()}>
                Cancelar
              </MDBBtn>
              <MDBBtn color="primary" onClick={() => this.doSave()}>
                {isEdit ? "Guardar cambios" : "Crear gasto periódico"}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    );
  }
}
